import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Link } from "../i18n/LangContext";
import { useTranslation } from "react-i18next";
import { FaCheck, FaLink } from "react-icons/fa";
import { localeFor, baseLang } from "../i18n";
import { getPostBySlug, getRelatedPosts } from "../services/blogService";
import StoryCard from "../components/blog/StoryCard";
import "../styles/blog/StoryPost.css";
import Seo from "../seo/Seo";
import CtaPair from "../components/CtaPair";
import { SITE_URL } from "../seo/site";
import { keepSuffixes } from "../utils/typography";
import { categoryName } from "../utils/categories";

const StoryPost = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [readingProgress, setReadingProgress] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const contentRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const postData = await getPostBySlug(slug);
        
        if (isMounted) {
          if (postData) {
            setPost(postData);
            setError(null);
            
            // Load related posts
            if (postData.tags && postData.tags.length > 0) {
              const related = await getRelatedPosts(slug, postData.tags, 3);
              setRelatedPosts(related);
            }
          } else {
            setError(t("stories.post.notFound"));
            setPost(null);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(t("stories.post.loadError"));
          console.error("Error loading post:", err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPost();

    return () => {
      isMounted = false;
    };
  }, [slug, lang, t]);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const contentTop = contentRef.current.offsetTop;
      const contentHeight = contentRef.current.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY;

      const progress = Math.min(
        100,
        Math.max(
          0,
          ((scrollTop + windowHeight - contentTop) / contentHeight) * 100
        )
      );

      setReadingProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [post]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(localeFor(i18n.language), {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const copyToClipboard = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };


  if (loading) {
    return (
      <div>
        <div className="hero_area"></div>
        <section className="story-post-wrapper layout_padding">
          <div className="container">
            <div className="story-post-container">
              <div className="loading-state">{t("stories.post.loading")}</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div>
        <Seo page="stories" path={`/stories/${slug}`} override={{ title: t("stories.post.notFound"), noindex: true }} />
        <div className="hero_area"></div>
        <section className="story-post-wrapper layout_padding">
          <div className="container">
            <div className="story-post-container">
              <div className="error-state">
                <h2>{t("stories.post.notFound")}</h2>
                <p>{error || t("stories.post.notFoundText")}</p>
                <Link to="/stories" className="btn-1">
                  {t("stories.post.back")}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const articleJsonLd = {
    "@type": "BlogPosting",
    "@id": `${SITE_URL}${window.location.pathname}#article`,
    headline: post.title,
    description: post.excerpt,
    image: post.featuredImage ? `${SITE_URL}${post.featuredImage}` : undefined,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    inLanguage: post.lang || baseLang(lang),
    keywords: (post.tags || []).join(", "),
    articleSection: categoryName(t, post.category),
    wordCount: post.content ? post.content.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length : undefined,
    author:
      post.author?.name && post.author.name !== "Mali Labs"
        ? { "@type": "Person", name: post.author.name, jobTitle: post.author.role, url: post.author.url || `${SITE_URL}/about`, worksFor: { "@id": `${SITE_URL}/#organization` } }
        : { "@type": "Organization", name: "Mali Labs", url: SITE_URL },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${window.location.pathname}` },
  };

  return (
    <div>
      <Seo
        page="stories"
        path={`/stories/${slug}`}
        override={{
          title: post.title,
          description: post.excerpt,
          image: post.featuredImage || undefined,
          type: "article",
          languages: post.languages,
          breadcrumbs: [{ name: t("nav.home"), path: "/" }, { name: t("nav.stories"), path: "/stories" }, { name: post.title, path: `/stories/${slug}` }],
          jsonLd: [articleJsonLd],
        }}
      />
      <div className="reading-progress-bar" ref={progressRef}>
        <div 
          className="reading-progress-fill" 
          style={{ width: `${readingProgress}%` }}
        ></div>
      </div>

      <div className="hero_area"></div>
      <section className="story-post-wrapper layout_padding">
        <div className="container">
          <div className="story-post-container">
            <nav className="breadcrumb-nav">
              <Link to="/" className="breadcrumb-link">{t("nav.home")}</Link>
              <span className="breadcrumb-separator">/</span>
              <Link to="/stories" className="breadcrumb-link">{t("nav.stories")}</Link>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">{post?.category ? categoryName(t, post.category) : t("stories.post.breadcrumbPost")}</span>
            </nav>

            <article className="story-post">
              {post.featuredImage && (
                <div className="story-post-featured-image">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    loading="eager"
                  />
                </div>
              )}
              <header className="story-post-header">
                {post.category && (
                  <span className="story-category">{categoryName(t, post.category)}</span>
                )}
                <h1 className="story-post-title">{keepSuffixes(post.title)}</h1>
                <div className="story-post-meta">
                  <div className="story-author">
                    {post.author.avatar ? (
                      <img
                        src={post.author.avatar}
                        alt={post.author.name}
                        className="author-avatar"
                      />
                    ) : (
                      <div className="author-avatar-placeholder">
                        {post.author.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="author-name">{post.author.name}</span>
                  </div>
                  <div className="story-info">
                    <span className="story-date">
                      {formatDate(post.publishedAt)}
                    </span>
                    {post.readingTime && (
                      <span className="story-reading-time">
                        {t("stories.readingTime", { count: post.readingTime })}
                      </span>
                    )}
                  </div>
                </div>
              </header>

              <div className="social-share">
                <button 
                  onClick={copyToClipboard}
                  className="social-share-btn"
                  title={copySuccess ? t("stories.post.copiedTitle") : t("stories.post.copyTitle")}
                >
                  {copySuccess ? (
                    <>
                      <FaCheck aria-hidden="true" />
                      <span className="share-btn-text">{t("stories.post.copied")}</span>
                    </>
                  ) : (
                    <>
                      <FaLink aria-hidden="true" />
                      <span className="share-btn-text">{t("stories.post.copy")}</span>
                    </>
                  )}
                </button>
              </div>

              {post.machineTranslated && baseLang(lang) !== "en" && (
                <p className="story-translation-note text-muted small" style={{ fontStyle: "italic" }}>
                  {t("stories.post.machineTranslated")}
                </p>
              )}

              <div
                ref={contentRef}
                className="story-post-content"
                dangerouslySetInnerHTML={{ __html: post.content ? post.content.replace(/\[image:\s*(.+?)\]/g, `<img src="$1" class="story-inline-image" alt="${t("stories.post.inlineImage")}" />`) : "" }}
              />

              {post.tags && post.tags.length > 0 && (
                <footer className="story-post-footer">
                  <div className="story-tags">
                    <span className="tags-label">{t("stories.post.tags")}</span>
                    {post.tags.map((tag, index) => (
                      <span key={index} className="story-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </footer>
              )}
            </article>

            {post.author?.name && post.author.name !== "Mali Labs" && (
              <aside className="author-box">
                {post.author.avatar && <img src={post.author.avatar} alt={post.author.name} className="author-box-avatar" loading="lazy" />}
                <div>
                  <span className="author-box-label">{t("stories.post.aboutAuthor")}</span>
                  <h3 className="author-box-name">{post.author.name}</h3>
                  {post.author.role && <p className="author-box-role">{post.author.role}</p>}
                  <Link to="/about" className="author-box-link">{t("nav.about")} →</Link>
                </div>
              </aside>
            )}

            <section className="story-cta">
              <h2>{t("stories.post.cta.title")}</h2>
              <p>{t("stories.post.cta.text")}</p>
              <CtaPair tone="dark" align="center" className="story-cta-actions" />
            </section>

            {relatedPosts.length > 0 && (
              <section className="related-posts">
                <h2 className="related-posts-title">{t("stories.post.related")}</h2>
                <p className="related-posts-subtitle">
                  {t("stories.post.relatedSubtitle")}
                </p>
                <div className="related-posts-list">
                  {relatedPosts.map((relatedPost) => (
                    <StoryCard key={relatedPost.id} post={relatedPost} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StoryPost;
